/**
 * API isteklerini yönetmek için debouncer yardımcı sınıfı
 * Aynı endpoint için eş zamanlı benzer istekleri engeller
 */
export class RequestDebouncer {
  constructor() {
    this.pendingRequests = new Map();
    this.requestTimeouts = new Map();
  }

  /**
   * Bir istek için benzersiz anahtar oluşturur
   * @param {string} endpoint - API endpoint'i
   * @param {Object} params - İstek parametreleri
   * @returns {string} Benzersiz istek anahtarı
   */
  createKey(endpoint, params) {
    return `${endpoint}|${JSON.stringify(params || {})}`;
  }

  /**
   * İsteğin yapılıp yapılmayacağını kontrol eder
   * @param {string} endpoint - API endpoint'i
   * @param {Object} params - İstek parametreleri
   * @param {number} debounceTime - Milisaniye cinsinden debounce süresi (varsayılan: 200ms)
   * @returns {boolean} İstek yapılabilir ise true
   */
  shouldMakeRequest(endpoint, params, debounceTime = 200) {
    const key = this.createKey(endpoint, params);
    
    // Zaten bekleyen istek varsa, yeni isteği engelle
    if (this.pendingRequests.has(key)) {
      return false;
    }
    
    // Son isteğin üzerinden debounceTime geçmemişse, isteği engelle
    const lastTimeout = this.requestTimeouts.get(key);
    if (lastTimeout) {
      clearTimeout(lastTimeout);
    }
    
    // Yeni bir timeout ayarla
    const timeoutId = setTimeout(() => {
      this.requestTimeouts.delete(key);
    }, debounceTime);
    
    this.requestTimeouts.set(key, timeoutId);
    
    // İsteğin yapılabileceğini belirt
    return true;
  }
  
  /**
   * İstek başladığında çağrılır
   * @param {string} endpoint - API endpoint'i
   * @param {Object} params - İstek parametreleri
   */
  startRequest(endpoint, params) {
    const key = this.createKey(endpoint, params);
    this.pendingRequests.set(key, true);
  }
  
  /**
   * İstek tamamlandığında çağrılır
   * @param {string} endpoint - API endpoint'i
   * @param {Object} params - İstek parametreleri
   */
  endRequest(endpoint, params) {
    const key = this.createKey(endpoint, params);
    this.pendingRequests.delete(key);
  }
  
  /**
   * İstek zaten yapılıyor mu kontrol eder
   * @param {string} endpoint - API endpoint'i
   * @param {Object} params - İstek parametreleri
   * @returns {boolean} İstek zaten yapılıyorsa true
   */
  isRequestPending(endpoint, params) {
    const key = this.createKey(endpoint, params);
    return this.pendingRequests.has(key);
  }
}

/**
 * Singleton request debouncer
 */
export const requestDebouncer = new RequestDebouncer();